import { useState } from 'react';
import { IconLogout2 } from '@tabler/icons-react';
import { Link, Outlet } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Button,
  Group,
  Image,
  NavLink,
  rem,
  ScrollArea,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuContext } from '@/hooks/organizerHooks/useMenuContext';
import { dashboardHomePath } from '@/utils/dashboardHome';
import { isMenuVisible } from '@/utils/menuVisibility';
import { getLogoSvg } from '../Logo/LogoSvg';
import { ThemeSchemeToggle } from '../ThemeSchemeToggle/ThemeSchemeToggle';
import { UserMenu } from '../UserMenu/UserMenu';
import { navigationLinks } from './navigationLinks';

const DashboardLayout = () => {
  const [opened, { toggle, close }] = useDisclosure();
  const { colorScheme } = useMantineColorScheme();
  const { currentUser, logout } = useAuth();
  // Sem papel de organizador: as ferramentas aparecem com o primeiro evento próprio ou delegado
  const menuContext = useMenuContext();
  const logoSvg = getLogoSvg();
  const [activeItem, setActiveItem] = useState<{ menuId: number | null; submenuId: number | null }>(
    {
      menuId: null,
      submenuId: null,
    }
  );
  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{
        width: { base: 300 },
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      pt="md"
    >
      <AppShell.Header
        bg={colorScheme === 'dark' ? '#191919' : 'white'}
        style={{ border: 'none', boxShadow: 'none' }}
      >
        <Group h="100%" px="md" justify="space-between" align="center">
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Link to={dashboardHomePath(currentUser?.role)} style={{ textDecoration: 'none' }}>
            <Image w={185} src={logoSvg} alt="Brasil Arena" />
          </Link>

          <Group gap="xs">
            <ThemeSchemeToggle size={38} />
            <UserMenu size={38} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        bg={colorScheme === 'dark' ? '#191919' : 'white'}
        style={{
          borderTop: `1px solid ${colorScheme === 'dark' ? '#2e2e2e' : '#e9e9e9'}`,
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          boxShadow: 'none',
          justifyContent: 'space-between',
        }}
      >
        <ScrollArea type="never" pt="md">
          {navigationLinks
            .filter((menu) => isMenuVisible(menu.visibleTo ?? ['all'], menuContext))
            .map((menu) => (
              <NavLink
                p={10}
                mb={4}
                key={menu.id}
                label={menu.label}
                color={colorScheme === 'dark' ? '#f1f3f5' : '#3c3d3d'}
                leftSection={<menu.icon style={{ width: rem(19), height: rem(19) }} />}
                active={menu.id === activeItem.menuId}
                onClick={() => {
                  setActiveItem({ menuId: menu.id, submenuId: null });
                  close();
                }}
                autoContrast
                component={Link}
                to={menu.href}
                sx={(theme) => ({
                  position: 'relative',
                  textDecoration: 'none',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: theme.spacing.lg,
                  paddingRight: theme.spacing.lg,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  minHeight: '40px',

                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',

                    height: '4px',
                    borderRadius: '4px',
                    backgroundColor: theme.colors.principal[5],
                    bottom: -5,
                    left: '0',
                    transformOrigin: 'right',
                    transform: 'scaleX(0)',
                    transition: 'transform 0.3s ease-in-out',
                  },
                  '&:hover::before': {
                    transformOrigin: 'left',
                    transform: 'scaleX(1)',
                  },
                })}
              />
            ))}
        </ScrollArea>

        <Button
          m={20}
          fz={17}
          onClick={logout}
          component="a"
          color="gray"
          variant="subtle"
          href="/"
          leftSection={<IconLogout2 size={24} />}
        >
          SAIR
        </Button>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
};

export default DashboardLayout;
